'use client';

import Icon from '@/components/ui/AppIcon';
import DemoDataPill from './DemoDataPill';

// ─── Types ────────────────────────────────────────────────────────────────

type RebateStatus = 'expected' | 'partial' | 'received' | 'overdue';
type Manufacturer = 'GAF' | 'CertainTeed' | 'Owens Corning' | 'Tamko' | 'IKO' | 'Other';

interface Rebate {
  id: string;
  manufacturer: Manufacturer;
  rebateType: string;
  period: string;
  jobId?: string;
  amountExpected: number;
  amountReceived: number;
  status: RebateStatus;
  dateClaimed: string;     // YYYY-MM-DD
  dateReceived?: string;   // YYYY-MM-DD
}

interface Props {
  onAddRebate: () => void;
}

// ─── Mock data ────────────────────────────────────────────────────────────
// Sums (verified against header):
//   gross expected (all rows):   $74,500
//   received YTD (received_date in current year): $20,100
//   aging risk (>90d open):      1 (CertainTeed Promo HDZ claimed Feb 10)
//   distinct manufacturers:      5 (GAF / CertainTeed / Owens Corning / Tamko / IKO)
const MOCK_REBATES: Rebate[] = [];

// ─── Helpers ──────────────────────────────────────────────────────────────

const MFR_COLOR: Record<Manufacturer, string> = {
  'GAF':           '#C8102E',
  'CertainTeed':   '#00665E',
  'Owens Corning': '#E91D2C',
  'Tamko':         '#003DA5',
  'IKO':           '#1D1D1B',
  'Other':         '#6B7280',
};

function mfrInitial(m: Manufacturer): string {
  if (m === 'Other') return '?';
  return m.charAt(0).toUpperCase();
}

function formatMoney(n: number): string {
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (Math.abs(n) >= 1_000)     return `$${(n / 1_000).toFixed(1)}k`;
  return `$${n.toFixed(0)}`;
}

function statusPill(s: RebateStatus): { label: string; color: string; bg: string } {
  if (s === 'overdue')  return { label: 'Overdue',  color: '#B91C1C', bg: 'rgba(239,68,68,0.10)'  };
  if (s === 'expected') return { label: 'Expected', color: '#92400E', bg: 'rgba(245,158,11,0.10)' };
  if (s === 'partial')  return { label: 'Partial',  color: '#C2410C', bg: 'rgba(249,115,22,0.10)' };
  return                       { label: 'Received', color: '#047857', bg: 'rgba(16,185,129,0.10)' };
}

const STATUS_PRIORITY: Record<RebateStatus, number> = {
  overdue: 0,
  expected: 1,
  partial: 2,
  received: 3,
};

function isReceivedThisYear(r: Rebate): boolean {
  if (!r.dateReceived) return false;
  const currentYear = new Date().getUTCFullYear();
  return new Date(r.dateReceived + 'T00:00:00Z').getUTCFullYear() === currentYear;
}

function daysSinceClaimed(r: Rebate): number {
  const today = new Date(new Date().toISOString().slice(0, 10) + 'T00:00:00Z').getTime();
  const claimed = new Date(r.dateClaimed + 'T00:00:00Z').getTime();
  return Math.floor((today - claimed) / 86_400_000);
}

function isAgingRisk(r: Rebate): boolean {
  // Either explicitly marked overdue OR open (not received) and >90d since claimed.
  if (r.status === 'overdue') return true;
  if (r.status === 'received') return false;
  return daysSinceClaimed(r) > 90;
}

// ─── Main ─────────────────────────────────────────────────────────────────

export default function Row5_5MaterialRebates({ onAddRebate }: Props) {
  // Aggregates
  const totalExpected = MOCK_REBATES.reduce((s, r) => s + r.amountExpected, 0);
  const totalReceivedYTD = MOCK_REBATES
    .filter(isReceivedThisYear)
    .reduce((s, r) => s + r.amountReceived, 0);
  const distinctManufacturers = new Set(MOCK_REBATES.map((r) => r.manufacturer)).size;
  const agingRiskCount = MOCK_REBATES.filter(isAgingRisk).length;

  // Sort by status priority, then expected_date DESC within bucket.
  const sorted = [...MOCK_REBATES].sort((a, b) => {
    const p = STATUS_PRIORITY[a.status] - STATUS_PRIORITY[b.status];
    if (p !== 0) return p;
    return b.dateClaimed.localeCompare(a.dateClaimed);
  });

  return (
    <div
      className="relative rounded-xl p-5"
      style={{
        background: '#FFFFFF',
        boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
        border: '1px solid #F0F0F0',
      }}
    >
      <DemoDataPill />

      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-4 pr-16">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Icon name="CurrencyDollarIcon" size={15} style={{ color: '#10B981' }} />
            <h3 className="text-base font-bold" style={{ color: '#000150', fontFamily: 'Inter, Poppins, sans-serif' }}>
              Material Rebates
            </h3>
          </div>
          <p className="text-xs text-gray-400 mt-0.5">
            {formatMoney(totalExpected)} expected · {formatMoney(totalReceivedYTD)} received YTD
          </p>
        </div>
        <button
          onClick={onAddRebate}
          className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold text-white hover:opacity-90 active:scale-95 transition-all"
          style={{ background: '#07B0E9' }}
          suppressHydrationWarning
        >
          <Icon name="PlusIcon" size={13} />
          Add Rebate
        </button>
      </div>

      {/* Section A — Summary cards (3 across) */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        <SummaryCard
          label="Total Expected"
          value={formatMoney(totalExpected)}
          deltaLabel=""
          deltaPositive
          sub={`across ${distinctManufacturers} manufacturers`}
          tone="#F59E0B"
        />
        <SummaryCard
          label="Total Received YTD"
          value={formatMoney(totalReceivedYTD)}
          deltaLabel=""
          deltaPositive
          sub={`${MOCK_REBATES.filter(isReceivedThisYear).length} rebates paid`}
          tone="#10B981"
        />
        <SummaryCard
          label="Aging Risk"
          value={String(agingRiskCount)}
          deltaLabel={agingRiskCount > 0 ? `${agingRiskCount} >90d open` : 'All on track'}
          deltaPositive={agingRiskCount === 0}
          sub="rebates aged >90d"
          tone={agingRiskCount > 0 ? '#EF4444' : '#10B981'}
          showBadge={agingRiskCount > 0}
        />
      </div>

      {/* Section B — Detail table */}
      <div className="overflow-x-auto -mx-2">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-gray-500 uppercase tracking-wider border-b border-gray-100">
              <th className="px-2 py-2 font-semibold">Manufacturer</th>
              <th className="px-2 py-2 font-semibold">Period / Type</th>
              <th className="px-2 py-2 font-semibold">Job</th>
              <th className="px-2 py-2 font-semibold text-right">Expected</th>
              <th className="px-2 py-2 font-semibold text-right">Received</th>
              <th className="px-2 py-2 font-semibold">Status</th>
              <th className="px-2 py-2 font-semibold">Claimed</th>
              <th className="px-2 py-2 font-semibold">Received</th>
              <th className="px-2 py-2 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {sorted.map((r) => {
              const pill = statusPill(r.status);
              return (
                <tr key={r.id} className="hover:bg-gray-50/50 transition-colors">
                  <td className="px-2 py-2.5">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0"
                        style={{ background: MFR_COLOR[r.manufacturer] }}
                      >
                        {mfrInitial(r.manufacturer)}
                      </div>
                      <span className="text-xs font-semibold text-gray-800 truncate">{r.manufacturer}</span>
                    </div>
                  </td>
                  <td className="px-2 py-2.5">
                    <p className="text-xs font-semibold text-gray-700">{r.period}</p>
                    <p className="text-[10px] text-gray-400">{r.rebateType}</p>
                  </td>
                  <td className="px-2 py-2.5">
                    {r.jobId ? (
                      <span className="text-[11px] font-mono text-gray-600">{r.jobId}</span>
                    ) : (
                      <span className="text-[11px] text-gray-300">—</span>
                    )}
                  </td>
                  <td className="px-2 py-2.5 text-right font-mono text-xs text-gray-700">
                    {formatMoney(r.amountExpected)}
                  </td>
                  <td className="px-2 py-2.5 text-right font-mono text-xs text-gray-700">
                    {formatMoney(r.amountReceived)}
                  </td>
                  <td className="px-2 py-2.5">
                    <span
                      className="inline-block rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide"
                      style={{ background: pill.bg, color: pill.color }}
                    >
                      {pill.label}
                    </span>
                  </td>
                  <td className="px-2 py-2.5 text-[11px] text-gray-500">{r.dateClaimed}</td>
                  <td className="px-2 py-2.5 text-[11px] text-gray-500">
                    {r.dateReceived ?? <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-2 py-2.5 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => console.log('[Financial:Rebates] Edit (no-op):', r.id)}
                        className="w-6 h-6 rounded flex items-center justify-center text-gray-400 hover:text-[#0369A1] hover:bg-gray-100 transition-colors"
                        suppressHydrationWarning
                        title="Edit (no-op)"
                      >
                        <Icon name="PencilSquareIcon" size={12} />
                      </button>
                      <button
                        onClick={() => console.log('[Financial:Rebates] Delete (no-op):', r.id)}
                        className="w-6 h-6 rounded flex items-center justify-center text-gray-400 hover:text-[#B91C1C] hover:bg-gray-100 transition-colors"
                        suppressHydrationWarning
                        title="Delete (no-op)"
                      >
                        <Icon name="XMarkIcon" size={12} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Summary card sub-component ───────────────────────────────────────────

function SummaryCard({
  label,
  value,
  deltaLabel,
  deltaPositive,
  sub,
  tone,
  showBadge,
}: {
  label: string;
  value: string;
  deltaLabel: string;
  deltaPositive: boolean;
  sub: string;
  tone: string;
  showBadge?: boolean;
}) {
  return (
    <div
      className="rounded-lg p-3 relative"
      style={{ background: 'rgba(248,250,252,0.7)', border: '1px solid #F3F4F6' }}
    >
      {showBadge && (
        <span
          className="absolute top-2 right-2 rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase"
          style={{ background: 'rgba(239,68,68,0.10)', color: '#B91C1C' }}
        >
          Action
        </span>
      )}
      <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-500">{label}</p>
      <p
        className="text-2xl font-bold leading-tight mt-0.5"
        style={{ color: tone, fontFamily: 'Inter, Poppins, sans-serif' }}
      >
        {value}
      </p>
      <p className="text-[11px] font-medium mt-0.5" style={{ color: deltaPositive ? '#10B981' : '#B91C1C' }}>
        <span className="font-bold mr-1">{deltaPositive ? '▲' : '▼'}</span>{deltaLabel}
      </p>
      <p className="text-[10px] text-gray-400 mt-0.5">{sub}</p>
    </div>
  );
}
